import type {
  IHistogramItem,
  IHistogramItemResponse,
  IHistograms,
  IHistogramsGroup,
  IHistogramsGroupResponse,
  IHistogramsResponse
} from "~/types/IImpactV3Histograms";
import { useApiService } from "./useApiService";
import type {
  IImpactCountryRes,
  IImpactGeoLocalizationResponse,
  IImpactReferrerRes
} from "~/types/api";

/**
 * This composable gets the info from the Impact V5
 * @returns
 */
export const useImpactV3Service = () => {
  function mapToHistograms(data: IHistogramsResponse): IHistograms {
    return {
      researchTopicId: data.researchTopicId,
      defaultViewScaleOption: data.defaultViewScaleOption,

      totalViews: data.totalViews ? parseInt(data.totalViews) : 0,
      histogramGroups: mapToHistogramsGroup(data.histogramGroups || [])
    };
  }

  function mapToHistogramsGroup(
    group: IHistogramsGroupResponse[]
  ): IHistogramsGroup[] {
    const histogramGroups: IHistogramsGroup[] = [];
    group.map((group) => {
      histogramGroups.push({
        timeGranularity: group.timeGranularity,
        totalViewsCount: group.totalViewsCount
          ? parseInt(group.totalViewsCount)
          : 0,
        articleViewsCount: group.articleViewsCount
          ? parseInt(group.articleViewsCount)
          : 0,
        articleDownloadsCount: group.articleDownloadsCount
          ? parseInt(group.articleDownloadsCount)
          : 0,
        topicViewsCount: group.topicViewsCount
          ? parseInt(group.topicViewsCount)
          : 0,
        histogramItems: mapToHistogramItems(
          group.histogramItems,
          group.timeGranularity
        )
      });
    });
    return histogramGroups;
  }

  function mapToHistogramItems(
    items: IHistogramItemResponse[],
    timeGranularity: string
  ): IHistogramItem[] {
    const histogramItems: IHistogramItem[] = [];
    items.map((item) => {
      histogramItems.push({
        dateKey: item.dateKey,
        period:
          timeGranularity === "Last 30 days"
            ? item.period.split(" ")[1]
            : item.period,
        totalViewsCount: item.totalViewsCount
          ? parseInt(item.totalViewsCount)
          : 0,
        articleViewsCount: item.articleViewsCount
          ? parseInt(item.articleViewsCount)
          : 0,
        articleDownloadsCount: item.articleDownloadsCount
          ? parseInt(item.articleDownloadsCount)
          : 0,
        topicViewsCount: item.topicViewsCount
          ? parseInt(item.topicViewsCount)
          : 0
      });
    });
    return histogramItems.reverse();
  }

  const { impactApiUrl } = useRuntimeConfig().public;
  const { getFetchData } = useApiService();

  const getHistogram = async (rtId: number): Promise<IHistograms | null> => {
    const data = await getFetchData<IHistogramsResponse>(
      `${impactApiUrl}/ResearchTopics(${rtId})/HistogramsPublic`,
      "HistogramsPublic"
    );

    return mapToHistograms(data);
  };

  const getGeolocations = async (
    researchTopicId: number
  ): Promise<IImpactGeoLocalizationResponse> => {
    const apiUrl = `${impactApiUrl}/research-topic/PublicationGeolocalizations?researchTopicId=${researchTopicId}`;

    return getFetchData(apiUrl, "getGeolocations", false);
  };

  const getTopCountries = async (
    researchTopicId: number
  ): Promise<IImpactCountryRes> => {
    const impactApiUrl = useRuntimeConfig().public.impactApiUrl;
    const apiUrl = `${impactApiUrl}/ResearchTopics/CountryTop?researchTopicId=${researchTopicId}`;

    return getFetchData(apiUrl, "getTopCountries", false);
  };

  const getTopReferrers = (
    researchTopicId: number
  ): Promise<IImpactReferrerRes> => {
    const impactApiUrl = useRuntimeConfig().public.impactApiUrl;
    const apiUrl = `${impactApiUrl}/ResearchTopics/ReferrerTop?researchTopicId=${researchTopicId}`;

    return getFetchData(apiUrl, "getTopReferrers", false);
  };

  return { getHistogram, getGeolocations, getTopCountries, getTopReferrers };
};
